import { useQueries } from "@tanstack/react-query";
import { inboxApi } from "views/inbox/api/api";
import { inboxKeys } from "views/inbox/api/hooks";
import { useInboxToken } from "views/inbox/api/useInboxToken";
import type { Applicant } from "types/application";

const POLL_INTERVAL_MS = 15_000;

/**
 * Total unread messages across every applicant on an application, for the Chat
 * button badge in the application header (CRM-1133).
 *
 * Mirrors the per-applicant `useApplicantChatUnreads` but batched with
 * `useQueries`, so the applicant count can vary between renders without breaking
 * the rules of hooks. Uses `useConversationByContact`'s key, so these and the
 * ApplicantsTable badges share one cache entry per contact.
 */
export const useApplicationChatUnreads = (applicants: Applicant[]): number => {
  const { accessToken } = useInboxToken();

  const contactIds = applicants
    .map((applicant) => applicant.crm_contact_id)
    .filter((contactId): contactId is number => Boolean(contactId));

  const conversationQueries = useQueries({
    queries: contactIds.map((contactId) => ({
      queryKey: [...inboxKeys.contacts(), contactId, "CONVERSATION"],
      queryFn: () => inboxApi.getConversationByContact(contactId),
      enabled: !!accessToken,
      refetchInterval: POLL_INTERVAL_MS
    }))
  });

  return conversationQueries.reduce(
    (sum, query) => sum + (query.data?.conversation.unread_count ?? 0),
    0
  );
};
